import { mdiArrowTopRight, mdiFilterVariant, mdiPaperclip } from "@mdi/js";
import Icon from "@mdi/react";
import debounce from "lodash.debounce";
import moment from "moment";
import { FunctionComponent, useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import LoadingWidget from "../../../context_providers/modal/loader";
import {
  useToast,
  ToastType,
} from "../../../context_providers/toast/toast_ctx";
import SearchFieldWidget from "../../../ui/new_components/search_field";
import { UTCToLocalDate } from "../../../utils/date_util";
import {
  PurchaseVoucherFilter,
  PurchaseVoucherModelV2,
} from "../models/purchase_model";
import { urls } from "../../../utils/urls";
import {
  GetPurchaseVoucherList,
  normalizePoNumberSearch,
} from "./repo/purchase_voucher_repo";
import {
  VendorMultiFilter,
  WarehouseMultiFilter,
} from "../../../ui/new_components/entity_selects/EntityMultiFilters";
import ModalDialog from "../../../ui/new_components/common/modal";
import ExportGRN from "./purchase_voucher_export";
import Pagination from "../../../ui/new_components/common/pagination";
import { formatAmountWithCommas } from "../../../ui/pages/content_panel/utils/helpers";
import { AttachmentModel } from "../../purchase_order/model/purchase_order_model";
import DrawerWidget from "../../../context_providers/modal/drawer_widget";
import {
  ModalAlignment,
  ModalType,
} from "../../../context_providers/modal/modal_context";
import InwardAttachmentViewSection from "../vouchers/purchase/components/inward_attachment_view_widget";
import { GetVoucherAttachments } from "../vouchers/purchase/service/inward_repo";
import {
  ResponsiveListShell,
  FilterSheet,
  DateRangeFilter,
  MobileRecordCard,
  MobileRecordCardList,
  StatusBadge,
  EmptyState,
  TextFilterInput,
} from "../../../ui/new_components/common/responsive";

interface PurchaseVoucherListScreenProps {}

const PurchaseVoucherListScreen: FunctionComponent<
  PurchaseVoucherListScreenProps
> = () => {
  const navigation = useHistory();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const tempFilter = localStorage.getItem("grn_filter");

  const [count, setCount] = useState(20);
  const [total, setTotal] = useState(0);
  const [purchaseVoucherData, setPurchaseVoucherData] = useState<
    PurchaseVoucherModelV2[]
  >([]);

  const defaultFilter = {
    all: false,
    count: 20,
    page: 1,
    selectedVendors: [],
    selectedWarehouses: [],
  };

  const [showFilter, setShowFilter] = useState(
    () => typeof window !== "undefined" && window.innerWidth >= 768,
  );
  const [fetchedAt, setFetchedAt] = useState<Date>();
  const [filter, setFilter] = useState<PurchaseVoucherFilter>(
    tempFilter ? JSON.parse(tempFilter) : defaultFilter,
  );

  const getInwardStatus = (item: PurchaseVoucherModelV2) =>
    (item?.total_received_qty ?? 0) === 0
      ? "Pending"
      : (item?.total_po_qty ?? 0) > (item?.total_received_qty ?? 0)
        ? "Partial"
        : "Completed";

  const [showAttachment, setShowAttachment] = useState(false);
  const [voucherAttachments, setVoucherAttachments] = useState<
    AttachmentModel[]
  >([]);

  const fetchData = async (prop: PurchaseVoucherFilter) => {
    setLoading(true);

    let processedProp = {
      ...prop,
      warehouses: prop.selectedWarehouses?.map((w) => w) || [],
      vendors: prop.selectedVendors?.map((v) => v) || [],
    };
    delete processedProp.selectedWarehouses;
    delete processedProp.selectedVendors;
    try {
      const res = await GetPurchaseVoucherList(prop);

      if (res.success) {
        setCount(res.data.metadata?.count);
        setPurchaseVoucherData(res.data.data);
        setFetchedAt(new Date());

        if (res.data.metadata) {
          setTotal(res.data.metadata.total);
        }
      } else {
        showToast({ type: ToastType.error, text: res.error });
      }
    } catch (error: any) {
      showToast({ type: ToastType.error, text: error.message });
    }
    setLoading(false);
  };

  const debouncedHandler = useCallback(
    debounce((prop: PurchaseVoucherFilter) => {
      setPurchaseVoucherData([]); // Reset data if no filter
      fetchData(prop);
    }, 500),
    [],
  );

  const onSelect = (d: PurchaseVoucherModelV2) => {
    navigation.push(
      urls.purchase_voucher +
        "/detail-screen/" +
        d.id +
        `?po_no=${d.order_detail?.po_no ?? ""}`,
    );
  };

  useEffect(() => {
    localStorage.setItem("grn_filter", JSON.stringify(filter));
  }, [filter]);

  useEffect(() => {
    debouncedHandler(filter);
  }, [filter, debouncedHandler]);

  const getVoucherAttachments = async (voucher_id: string) => {
    setLoading(true);
    try {
      const res = await GetVoucherAttachments(voucher_id);
      setVoucherAttachments(res.data.attachments);
      if (res.success) {
        setLoading(false);
      } else {
        showToast({ type: ToastType.error, text: res.error });
      }
    } catch (error) {
      showToast({
        type: ToastType.error,
        text: "An error occurred while fetching Inward data.",
      });
    } finally {
      setLoading(false);
    }
  };

  const clearLocalStore = () => {
    // Remove from localStorage
    localStorage.removeItem("grn_filter");

    // Reset filter state
    const defaultFilterObj = defaultFilter;
    setFilter(defaultFilterObj);

    // Trigger UI updates and fetch
    debouncedHandler(defaultFilterObj);
    fetchData(defaultFilterObj);
  };

  return (
    <>
      {showAttachment && (
        <DrawerWidget
          data={{
            id: 1,
            title: "Attachments",
            type: ModalType.drawer,
            alignment: ModalAlignment.right,
            container: (
              <InwardAttachmentViewSection
                attachments={voucherAttachments}
                onSelect={(att: AttachmentModel) => {
                  let url = att.url;
                  window.open(url, "_blank", "noopener,noreferrer");
                }}
              />
            ),
          }}
          onClose={function (): void {
            setShowAttachment(false);
          }}
        ></DrawerWidget>
      )}
      {showExport && (
        <ModalDialog
          show={showExport}
          title="Export GRN"
          onClose={() => setShowExport(false)}
        >
          <ExportGRN filter={filter} onClose={() => setShowExport(false)} />
        </ModalDialog>
      )}

      <ResponsiveListShell
        title="Goods Received Note"
        count={total}
        fetchedAt={fetchedAt}
        onRefresh={() => fetchData(filter)}
        search={
          <SearchFieldWidget
            placeholder="Search GRN"
            defaulValue={filter?.gr_number ?? ""}
            onClear={() => setFilter((p) => ({ ...p, gr_number: "" }))}
            onSubmit={(value) => setFilter((p) => ({ ...p, gr_number: value }))}
          />
        }
        actions={
          <>
            <button
              onClick={() => setShowExport(true)}
              className="flex gap-1 items-center cursor-pointer rounded p-1 bg-myPrimaryColor hover:scale-105 duration-200 text-white transform text-xs"
            >
              <Icon path={mdiArrowTopRight} className="h-4 w-4"></Icon>Export
            </button>
            <button
              className="flex gap-1 items-center cursor-pointer rounded p-1 bg-myPrimaryColor hover:scale-105 duration-200 text-xs text-white transform"
              onClick={() => clearLocalStore()}
            >
              Clear Filter
            </button>
          </>
        }
        filterToggle={
          <div className="flex gap-2 items-center">
            <h6 className="font-normal text-xs">Filters</h6>
            <div
              onClick={() => setShowFilter((v) => !v)}
              className="flex items-center justify-center bg-gray-200 p-1 rounded-full hover:bg-gray-800 hover:text-white cursor-pointer"
            >
              <Icon path={mdiFilterVariant} className="w-4 h-4" />
            </div>
          </div>
        }
      >
        <FilterSheet
          show={showFilter}
          onClose={() => setShowFilter(false)}
          onApply={() => setShowFilter(false)}
          onReset={() => {
            setFilter(defaultFilter);
          }}
          gridClassName="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-3 gap-y-2 items-start"
        >
          <div>
            <label>GRN Date</label>
            <DateRangeFilter
              compact
              showChips={false}
              fromDate={
                filter.fromDate
                  ? moment(filter.fromDate).format("YYYY-MM-DD")
                  : undefined
              }
              toDate={
                filter.toDate
                  ? moment(filter.toDate).format("YYYY-MM-DD")
                  : undefined
              }
              onFromChange={(v) => {
                if (!v) {
                  setFilter((o) => ({ ...o, fromDate: undefined }));
                  return;
                }
                const now = UTCToLocalDate(v)!;
                const fromDate = new Date(new Date(now).setHours(0, 0, 0, 0));
                setFilter((o) => ({ ...o, fromDate }));
              }}
              onToChange={(v) => {
                if (!v) {
                  setFilter((o) => ({ ...o, toDate: undefined }));
                  return;
                }
                const now = UTCToLocalDate(v)!;
                const toDate = new Date(
                  new Date(now).setHours(23, 59, 59, 999),
                );
                setFilter((o) => ({ ...o, toDate }));
              }}
            />
          </div>

          <div id="grn_list_warehouse_filter">
            <label>Warehouse</label>
            <WarehouseMultiFilter
              values={filter.selectedWarehouses}
              onChange={(selectedWarehouses) =>
                setFilter((o) => ({ ...o, page: 1, selectedWarehouses }))
              }
            />
          </div>

          <div id="grn_list_po_number_filter">
            <label>PO Number</label>
            <TextFilterInput
              placeholder="Enter PO number"
              value={filter.po_number}
              onChange={(value) =>
                setFilter((o) => ({
                  ...o,
                  page: 1,
                  po_number: normalizePoNumberSearch(value),
                }))
              }
            />
          </div>

          <div id="grn_list_vendor_filter">
            <label>Vendor</label>
            <VendorMultiFilter
              values={filter.selectedVendors}
              onChange={(selectedVendors) =>
                setFilter((o) => ({ ...o, page: 1, selectedVendors }))
              }
            />
          </div>

          <div>
            <label>Delivery Date</label>
            <DateRangeFilter
              compact
              showChips={false}
              fromLabel="Delivery From"
              toLabel="Delivery To"
              fromDate={
                filter.delivery_from
                  ? moment(filter.delivery_from).format("YYYY-MM-DD")
                  : undefined
              }
              toDate={
                filter.delivery_to
                  ? moment(filter.delivery_to).format("YYYY-MM-DD")
                  : undefined
              }
              onFromChange={(v) => {
                if (!v) {
                  setFilter((o) => ({ ...o, delivery_from: undefined }));
                  return;
                }
                const now = UTCToLocalDate(v)!;
                const delivery_from = new Date(
                  new Date(now).setHours(0, 0, 0, 0),
                );
                setFilter((o) => ({ ...o, delivery_from }));
              }}
              onToChange={(v) => {
                if (!v) {
                  setFilter((o) => ({ ...o, delivery_to: undefined }));
                  return;
                }
                const now = UTCToLocalDate(v)!;
                const delivery_to = new Date(
                  new Date(now).setHours(0, 0, 0, 0),
                );
                setFilter((o) => ({ ...o, delivery_to }));
              }}
            />
          </div>
        </FilterSheet>

        {purchaseVoucherData?.length > 0 ? (
          <>
            <div className="hidden md:block my-2 border">
              <div className="overflow-auto">
                <table className="w-full text-xs divide-y divide-gray-200 relative">
                  <thead className="text-xs py-2 sticky -top-1 bg-white z-10">
                    <tr className="border">
                      <th className="px-3 py-3 whitespace-nowrap text-left">
                        GRN
                      </th>
                      <th className="px-3 py-3 whitespace-nowrap text-left">
                        PO Number
                      </th>
                      <th className="px-3 py-3 whitespace-nowrap text-left">
                        Invoice Number
                      </th>
                      <th className="px-3 py-3 whitespace-nowrap text-left">
                        Vendor Name
                      </th>
                      <th className="px-3 py-3 whitespace-nowrap text-left">
                        Warehouse
                      </th>
                      <th className="px-3 py-3 whitespace-nowrap text-left">
                        Inward Date
                      </th>
                      <th className="px-3 py-3 whitespace-nowrap text-right">
                        Total PO QTY
                      </th>
                      <th className="px-3 py-3 whitespace-nowrap text-right">
                        Total Received QTY
                      </th>
                      <th className="px-3 py-3 whitespace-nowrap text-left">
                        Status
                      </th>
                      <th className="px-3 py-3 whitespace-nowrap text-left">
                        Unique Ids
                      </th>
                      <th className="px-3 py-3 whitespace-nowrap text-left">
                        Created At
                      </th>
                      <th className="px-3 py-3 whitespace-nowrap text-left">
                        Created By
                      </th>
                      <th className="px-3 py-3 whitespace-nowrap text-left">
                        Attachment
                      </th>
                    </tr>
                  </thead>
                  <tbody className="">
                    {purchaseVoucherData.map((data, i) => {
                      return (
                        <tr className="border text-center" key={i}>
                          <td
                            className="px-3 py-3 whitespace-nowrap text-left cursor-pointer text-blue-600 hover:underline"
                            role="button"
                            onClick={() => onSelect(data)}
                          >
                            {data.id}
                          </td>

                          <td className="px-3 py-3 whitespace-nowrap text-left">
                            {data?.order_detail?.po_no}
                          </td>

                          <td className="px-3 py-3 whitespace-nowrap text-left">
                            {data?.order_detail?.other_ref ?? ""}
                          </td>

                          <td className="px-3 py-3 whitespace-nowrap text-left">
                            {data?.party_detail?.name ?? ""}
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap text-left">
                            {data?.warehouse?.name ?? ""}
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap text-left">
                            {moment(data?.due_date).format("DD-MM-YYYY")}
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap text-right">
                            {formatAmountWithCommas(data?.total_po_qty) ?? 0}
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap text-right">
                            {formatAmountWithCommas(data?.total_received_qty) ??
                              0}
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap text-left">
                            <StatusBadge
                              status={getInwardStatus(data)}
                              kind="generic"
                            />
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap text-left">
                            {data?.uniqueIdsCount
                              ? data.uniqueIdsCount + "Unique Ids "
                              : " "}
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap text-left">
                            {moment(data?.created_at).format("DD-MM-YYYY")}
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap text-left">
                            {data?.created_by_name ?? "--"}
                          </td>

                          <td className="px-3 py-3 whitespace-nowrap text-left">
                            <button
                              onClick={() => {
                                setShowAttachment(true);
                                getVoucherAttachments(data.id);
                              }}
                              className="text-sm font-semibold text-blue-400 hover:text-blue-500 whitespace-nowrap flex items-center gap-1"
                            >
                              View
                              <span>
                                ({data?.attachments?.length}{" "}
                                {data?.attachments?.length === 1
                                  ? "Attachment"
                                  : "Attachments"}{" "}
                                )
                              </span>
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <MobileRecordCardList>
              {purchaseVoucherData.map((data, i) => (
                <MobileRecordCard
                  key={i}
                  onClick={() => onSelect(data)}
                  title={`${data.id}`}
                  status={
                    <StatusBadge
                      status={getInwardStatus(data)}
                      kind="generic"
                    />
                  }
                  fields={[
                    { label: "PO Number", value: data?.order_detail?.po_no },
                    {
                      label: "Invoice Number",
                      value: data?.order_detail?.other_ref ?? "-",
                    },
                    { label: "Vendor", value: data?.party_detail?.name ?? "-" },
                    { label: "Warehouse", value: data?.warehouse?.name ?? "-" },
                    {
                      label: "Delivery Date",
                      value: moment(data?.due_date).format("DD-MM-YYYY"),
                    },
                    {
                      label: "Total PO QTY",
                      value: formatAmountWithCommas(data?.total_po_qty) ?? 0,
                    },
                    {
                      label: "Received QTY",
                      value:
                        formatAmountWithCommas(data?.total_received_qty) ?? 0,
                    },
                    {
                      label: "Unique Ids",
                      value: data?.uniqueIdsCount
                        ? `${data.uniqueIdsCount} Unique Ids`
                        : "-",
                    },
                    {
                      label: "Created At",
                      value: moment(data?.created_at).format("DD-MM-YYYY"),
                    },
                    {
                      label: "Created By",
                      value: data?.created_by_name ?? "-",
                    },
                    {
                      label: "Attachments",
                      value: `${data?.attachments?.length ?? 0}`,
                    },
                  ]}
                  actions={[
                    {
                      icon: mdiPaperclip,
                      title: "View Attachments",
                      onClick: () => {
                        setShowAttachment(true);
                        getVoucherAttachments(data.id);
                      },
                    },
                  ]}
                />
              ))}
            </MobileRecordCardList>

            <div className="mt-3 sticky bottom-0 bg-white z-10 border-t flex justify-between items-center px-4 py-2">
              <Pagination
                itemCount={count}
                page={filter?.page}
                total={total}
                count={filter?.count}
                onChange={(page) => setFilter((o) => ({ ...o, page }))}
              />
            </div>
          </>
        ) : !loading ? (
          <EmptyState
            title="No GRNs found!"
            message="Try adjusting your search or filters."
            imageSrc="/images/nodata.png"
          />
        ) : null}
      </ResponsiveListShell>

      {loading && <LoadingWidget></LoadingWidget>}
    </>
  );
};

export default PurchaseVoucherListScreen;
